import { AUTH_PROVIDER_COOKIE } from '@/lib/auth-constants';
import { AUTH0_SESSION_COOKIE } from '@/lib/auth0/server-cookies';
import { AuthProvider } from '@heidi/auth/contracts';
import type { IncomingMessage } from 'http';

/** Subset of `_document` getInitialProps context used by the GTM route gate. */
export type GtmDocumentContext = {
  pathname?: string;
  asPath?: string;
  /** Parsed query of the document request; present on SSR, absent on static prerender. */
  query?: Record<string, string | string[] | undefined>;
  req?: IncomingMessage;
};

/** Pre-signup funnel routes where the GTM container may load (GRO-3977). */
const GTM_ALLOWED_PREFIXES = ['/register', '/login', '/onboarding', '/chat'] as const;

/**
 * Routes denied outright, checked before the funnel prefixes. The embed widget
 * is mounted by the marketing site with the clinician's question on its URL
 * (`/chat/embed?query=…`), and GA4 attaches the page URL to every event, so
 * GTM must never be on that document even though it sits under `/chat`
 * (ARCFE-1182).
 */
const GTM_DENIED_PREFIXES = ['/chat/embed'] as const;

const NEXTAUTH_SESSION_COOKIE = 'next-auth.session-token';
const NEXTAUTH_SECURE_SESSION_COOKIE = '__Secure-next-auth.session-token';
const KINDE_SESSION_COOKIE_RE = /^(access_token|id_token|refresh_token)\d*$/;
export const GTM_CONTAINER_ID = 'GTM-5VSCMP5N';

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Query-string keys that carry a clinician's free-text question. A document
 * whose URL has one of these never gets the GTM container, whatever the route:
 * GA4 attaches the page URL to every event and also reads `query` as a
 * site-search term (ARCFE-1182). Shared with the runtime bootstrap guard.
 */
// UNCARVED DOMAIN KEY (app-shell, ARCFE-1182): not a cache identity at all — these
// are URL query-parameter NAMES the GTM gate denies on. No queryKey, no mutationKey,
// nothing is keyed on this array; it only feeds a `URLSearchParams.has` check and the
// regex the bootstrap script is generated from. Analytics gating is app-shell, which
// has no package and no /contracts door.
export const GTM_DENIED_URL_PARAMS = ['query'] as const;

/** Case-sensitive key match on a raw query string; `?query=` and `&query=`. */
function hasDeniedQueryKey(search: string): boolean {
  if (!search) {
    return false;
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return GTM_DENIED_URL_PARAMS.some((key) => params.has(key));
}

function isGtmAllowedRoute(pathname: string): boolean {
  if (GTM_DENIED_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))) {
    return false;
  }

  if (pathname === '/') {
    return true;
  }

  return GTM_ALLOWED_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

function isAuthSessionCookieName(name: string): boolean {
  if (name === AUTH0_SESSION_COOKIE || name.startsWith(`${AUTH0_SESSION_COOKIE}__`)) {
    return true;
  }

  if (name === NEXTAUTH_SESSION_COOKIE || name === NEXTAUTH_SECURE_SESSION_COOKIE) {
    return true;
  }

  return KINDE_SESSION_COOKIE_RE.test(name);
}

/** Cookie-only session probe — no auth SDK roundtrip in _document. */
function hasAuthSessionCookie(req: IncomingMessage | undefined): boolean {
  const cookie = req?.headers?.cookie ?? '';
  if (!cookie) {
    return false;
  }

  const providers = Object.values(AuthProvider) as string[];
  for (const part of cookie.split(';')) {
    const trimmed = part.trim();
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx <= 0) {
      continue;
    }
    const name = trimmed.slice(0, eqIdx);
    const value = trimmed.slice(eqIdx + 1);
    if (name === AUTH_PROVIDER_COOKIE && providers.includes(value)) {
      return true;
    }
    if (isAuthSessionCookieName(name)) {
      return true;
    }
  }

  return false;
}

function buildClientSessionCookieGuard(): string {
  return (
    `n==='${AUTH0_SESSION_COOKIE}'||n.indexOf('${AUTH0_SESSION_COOKIE}__')===0` +
    `||n==='${NEXTAUTH_SESSION_COOKIE}'||n==='${NEXTAUTH_SECURE_SESSION_COOKIE}'` +
    `||/^(access_token|id_token|refresh_token)\\d*$/.test(n)`
  );
}

/**
 * Whether to inject the GTM container on this document request.
 *
 * Route allowlist is the primary gate (works on static prerender / export).
 * When `ctx.req` exists (SSR), suppress GTM for authenticated sessions.
 * Statically prerendered funnel HTML also guards at runtime via
 * `buildGtmBootstrapScript`.
 */
export function shouldLoadGtm(ctx: GtmDocumentContext): boolean {
  const raw = ctx.asPath ?? ctx.pathname ?? '/';
  const withoutHash = raw.split('#')[0] ?? raw;
  const [pathPart, ...searchParts] = withoutHash.split('?');
  const pathname = pathPart || '/';
  if (!isGtmAllowedRoute(pathname)) {
    return false;
  }

  // A clinical question on the URL denies GTM on every route, ahead of the
  // cookie probe: the unauthenticated funnel page is exactly where it shows up.
  // `ctx.query` is the authoritative parsed query on SSR; the raw path is the
  // fallback for callers that only have a URL string.
  const parsedHasDeniedKey =
    ctx.query != null && GTM_DENIED_URL_PARAMS.some((key) => key in ctx.query!);
  if (parsedHasDeniedKey || hasDeniedQueryKey(searchParts.join('?'))) {
    return false;
  }

  // Static prerender / static export: no request — route gate only; runtime
  // cookie guard in buildGtmBootstrapScript closes the authenticated-user gap.
  if (!ctx.req) {
    return true;
  }

  return !hasAuthSessionCookie(ctx.req);
}

/**
 * GTM bootstrap IIFE with a synchronous document.cookie guard for statically
 * prerendered funnel pages (ctx.req absent at build time).
 *
 * Session token cookies are HttpOnly and invisible here — the client-readable
 * `heidi-auth-provider` discriminator is the reliable runtime signal.
 *
 * The first line re-applies the clinical-question deny against the live
 * `location.search`: a statically exported document was gated at build time
 * with no query string, so `shouldLoadGtm` could not see one (ARCFE-1182).
 */
export function buildGtmBootstrapScript(): string {
  const providerValuesPattern = Object.values(AuthProvider).join('|');
  const sessionCookieGuard = buildClientSessionCookieGuard();

  const deniedUrlParamsPattern = GTM_DENIED_URL_PARAMS.join('|');

  return `(function(w,d,s,l,i){
if(/[?&](?:${deniedUrlParamsPattern})(?:=|&|$)/.test(w.location.search))return;
var c=d.cookie?d.cookie.split(';'):[],n,j,v;
for(j=0;j<c.length;j++){n=c[j].trim();var e=n.indexOf('=');if(e<=0)continue;v=n.slice(e+1);n=n.slice(0,e);
if(n==='${AUTH_PROVIDER_COOKIE}'&&/^(?:${providerValuesPattern})$/.test(v))return;
if(${sessionCookieGuard})return;}
w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`;
}
